import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import type FilterElementViewModelT from '@/ViewModels/Controls/FilterElementViewModel';
import type FilterSortElementViewModelT from '@/ViewModels/Controls/FilterSortElementViewModel';
import FilterChipsViewModelT from '@/Components/Filter/FilterChipsViewModel';
import type TextBoxViewModelT from '@/ViewModels/Controls/TextBoxViewModel';
import type LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import type ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import type LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import Utilities from '@/Utility/Utilities';
import { SortElement, SortComponent, FilterInfo, FilterJson, FilterComponent, FilterTerm, IFilterViewModel, IFilterViewModelConstructorParameter, GridColumn } from '#/DefaultViewExtensionInterfaces';
import ResourceManager from '@/Management/ResourceManager';
import FilterChipsHelper from '@/Components/Filter/FilterChipsHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

interface ISerializedFilter {
    filterElements: Array<any>;
    sortElements: Array<any>;
    activeChips: Array<any>;
    isVisible: boolean;
    editPublic: boolean;
}

/**
 * Das ViewModel für den Filter
 */
export default class FilterViewModel extends ControlViewModel implements IFilterViewModel {

    public filterChipsViewModel: FilterChipsViewModelT;
    public filterNameTextBoxViewModel: TextBoxViewModelT;
    public filterSaveIconViewModel: ImageViewModelT;
    public filterSaveLinkViewModel: LinkViewModelT;

    /**
     * Die auswählbaren Spalten
     */
    public columns: KnockoutObservableArray<GridColumn>;

    /**
     * Die Filterelemente
     */
    public elements: KnockoutObservableArray<FilterElementViewModelT>;

    /**
     * Die Sortier-Elemente
     */
    public sortElements: KnockoutObservableArray<FilterSortElementViewModelT>;

    public orderBy: KnockoutObservableArray<SortElement>;

    public includeAllPrincipals: boolean;
    public includeBasePrincipals: boolean;
    public includeSubPrincipals: boolean;
    
    /**
     * Die Anzahl der ms bis ein sich veränderter Filterausdruck per Knockout-Notification weitergegeben wird;
     * Kann nur einmalig bei der Initialisierung gesetzt werden!
     */
    public expressionTimeout: number;

    public serializedFilter: KnockoutObservable<string>;

    /**
     * Der aktuelle Filterausdruck
     */
    public filterExpressionObject: KnockoutComputed<FilterJson>;
    private notifyFilterExpressionObject: KnockoutObservable<any>;

    public lastMaxExtentCount: KnockoutObservable<number>;

    /**
     * Wird aufgerufen, wenn eines der Filter-Elemente meldet, dass es angewendet werden möchte.
     */
    public onSubmit: Function;

    private listViewPdoId: string;
    private pdTypeName: string;

    public preFilter: string;
    public staticFilter: string;
    public shouldShowChips: KnockoutObservable<boolean>;
    public shouldShowSaveInputs: KnockoutObservable<boolean>;
    public shouldShowSorting: KnockoutObservable<boolean>;
    public shouldEnableSorting: KnockoutObservable<boolean>;
    public editPublicChips: KnockoutObservable<boolean>;

    public preFilterTooltip: string;
    public isFilterLoadingDeactivated: KnockoutObservable<boolean>;
    public isLoading: KnockoutObservable<boolean>;

    constructor(params: IFilterViewModelConstructorParameter) {
        super(params);

        this.isFilterLoadingDeactivated = this.observableOrDefault(true);
        this.preFilter = this.paramOrDefault(params.preFilter, "");
        this.staticFilter = this.paramOrDefault(params.staticFilter, "");
        this.preFilterTooltip = this.paramOrDefault(params.preFilterTooltip, this.preFilter);

        this.shouldShowChips = this.observableOrDefault(params.shouldShowChips, true);
        this.shouldShowSaveInputs = this.observableOrDefault(params.shouldShowSaveInputs, true);
        this.shouldShowSorting = this.observableOrDefault(params.shouldShowSorting, true);
        this.shouldEnableSorting = this.observableOrDefault(true);

        this.columns = this.observableOrDefault(params.columns, []) as KnockoutObservableArray<any>;
        this.elements = this.observableOrDefault(params.elements, []) as KnockoutObservableArray<FilterElementViewModelT>;
        this.sortElements = this.observableOrDefault(params.sortElements, []) as KnockoutObservableArray<FilterSortElementViewModelT>;

        this.orderBy = this.observableOrDefault(params.orderBy, []) as KnockoutObservableArray<SortElement>;
        this.defineSubscription(this.orderBy, (orderBy: Array<SortElement>) => {
            this.removeSortElements();

            orderBy.forEach((sort, index) => {
                this.addNewSortElementWithParams({
                    sortColumn: sort.field,
                    sortDirection: sort.dir
                }, index, false);
            });

            this.rearrange();
        });

        this.includeAllPrincipals = this.paramOrDefault(params.includeAllPrincipals);
        this.includeBasePrincipals = this.paramOrDefault(params.includeBasePrincipals);
        this.includeSubPrincipals = this.paramOrDefault(params.includeSubPrincipals);

        this.defineSubscription(this.isVisible, isVisible => this.filterNameTextBoxViewModel.isVisible(isVisible));

        this.expressionTimeout = this.paramOrDefault(params.expressionTimeout, 1000);
        this.serializedFilter = this.observableOrDefault(params.serializedFilter, null);
        var parsedSerializedFilter = Utilities.isNullOrUndefinedOrBlank(this.serializedFilter()) ? null : JSON.parse(this.serializedFilter())
        this.editPublicChips = this.observableOrDefault(parsedSerializedFilter?.editPublic, false);

        let LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
        let labelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
        let ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
        let TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelT>('TextBoxViewModel');
        let FilterChipsViewModel = ComponentManager.getViewModel<typeof FilterChipsViewModelT>('FilterChips');

        this.listViewPdoId = params.listViewPdoId || null;
        this.pdTypeName = params.pdTypeName || null;

        this.lastMaxExtentCount = this.observableOrDefault(null);

        this.filterChipsViewModel = new FilterChipsViewModel({
            parentLocator: this.locator,
            locator: 'chip',
            sortAlphabetically: true,
            filter: this,
            listViewPdoId: this.listViewPdoId,
            pdTypeName: this.pdTypeName,
            preFilterTooltip: '',
            serializedFilter: parsedSerializedFilter,
            editPublicChips: this.editPublicChips,
            editPrivateChips: this.defineComputed(() => this.isVisible() && !this.editPublicChips()),
            publicUserData: params.publicUserData
        });

        this.filterChipsViewModel.isVisible = this.defineComputed(() => {
            const shouldShowChips = this.shouldShowChips();
            const hasChips = !_.isEmpty(this.filterChipsViewModel.values());
            // Wenn die FilterView offen ist, muss die ChipsArea angezeigt werden, falls die SaveFilterArea angezeigt werden soll, da diese sonst links steht
            const isFilterVisible = this.isVisible();
            const showSave = this.shouldShowSaveInputs();

            return (isFilterVisible && showSave) || (shouldShowChips && hasChips);
        });

        this.filterNameTextBoxViewModel = new TextBoxViewModel({
            value: this.filterChipsViewModel.value,
            onKeyUp: this.filterChipsViewModel.onKeyUp.bind(this.filterChipsViewModel),
            parentLocator: this.locator,
            locator: 'save-filter.textbox',
            placeholder: this.defineComputed(() => {
                return this.editPublicChips() ? ResourceManager.getInstance().get("SAVE_FILTER_INPUT_PLACEHOLDER_PUBLIC") : ResourceManager.getInstance().get("SAVE_FILTER_INPUT_PLACEHOLDER");
            })
        });
        this.filterSaveIconViewModel = new ImageViewModel({
            source: GlobalConfiguration.inlineImages.APPLY_OFF,
            alternativeText: labelViewModel.create('SAVE_FILTER_LINK_ALT_TEXT')
        });

        this.filterSaveLinkViewModel = new LinkViewModel({
            image: this.filterSaveIconViewModel,
            onClick: () => this.filterChipsViewModel.onKeyUp(13),
            parentLocator: this.locator,
            locator: 'save-filter.link'
        });

        // initialer Aufbau der FilterElemente muss vor der initialen Auswertung von this.expression geschehen
        this.init();

        this.isLoading = this.observableOrDefault(false);
        this.filterExpressionObject = this.createFilterExpressionObject();
        this.onSubmit = this.handlerOrDefault(params.onSubmit);
    }

    private init() {
        const filter = this.serializedFilter();
        if (filter == null || filter === '') {
            this.isVisible(false);
            return;
        }

        this.elements.removeAll();

        const serializedFilter: ISerializedFilter = JSON.parse(filter);

        if (serializedFilter) {
            if (serializedFilter.filterElements && serializedFilter.filterElements.length > 0) {
                serializedFilter.filterElements.forEach((newFilter, index) => {
                    this.addNewElementWithParameters({
                        connector: newFilter.connector,
                        operator: newFilter.operator,
                        column: newFilter.column,
                        value: newFilter.value
                    }, index, false);

                });
            }
            if (serializedFilter.sortElements && serializedFilter.sortElements.length > 0) {
                serializedFilter.sortElements.forEach((newSortElement, index) => {
                    this.addNewSortElementWithParams({
                        sortColumn: newSortElement.sortColumn,
                        sortDirection: newSortElement.sortDirection
                    }, index, false);

                });
            }
            if (serializedFilter.activeChips && serializedFilter.activeChips.length > 0) {
                this.filterChipsViewModel.ignoreChipsChange = true;
                this.filterChipsViewModel.activeChips(serializedFilter.activeChips);
                this.filterChipsViewModel.ignoreChipsChange = false;
            }

            if (serializedFilter.isVisible)
                this.isVisible(true);
            else
                this.isVisible(false);

            this.rearrange();
            this.refreshSorting();
        }
    }

    private createFilterExpressionObject(): KnockoutComputed<FilterJson> {

        // interne Function, welche direkt die FilterExpression berechnet, sofern nicht verhindert
        const calculateFilterExpression = () => {
            // FilterExpression nicht laden, während die FilterElemente noch gerendert werden
            if (this.isFilterLoadingDeactivated())
                return {};

            return this.forceFilterExpressionObjectCalculation();
        };

        // internes Computed mit einer Verzögerung (default 1s)
        const internalComputed = this.defineComputed(() => {
            this.isLoading(true);
            return calculateFilterExpression();
        }).extend({ rateLimit: { timeout: this.expressionTimeout, method: 'notifyWhenChangesStop' } });
        
        this.notifyFilterExpressionObject = this.observableOrDefault(null);

        // Computed Ergebnis: wird initial direkt geladen und ab dem zweiten mal mit Verzögerung 
        return this.defineComputed(() => {
            this.notifyFilterExpressionObject();
            let result;

            if (ko.computedContext.isInitial()) {
                result = calculateFilterExpression()
            } else {
                result = internalComputed();
            }

            this.isLoading(false);
            return result;
        });
    }

    /**
     * forciert die Berchnung des FilterExpressionObjects ohne dabei den ExpressionTimeout zu berücksichtigen
     */
    public forceFilterExpressionObjectCalculation() : FilterJson{
        // dependencies
        const elements = this.elements();
        elements.forEach((element) => {
            element.column();
            element.operator();
            element.connector();
            element.filterFactory();
            if (!!element.filterFactory()) {
                element.filterFactory().getFilterInfo();
            }
        });
        const sortElements = this.sortElements();
        sortElements.forEach((element) => {
            element.sortColumn();
            element.sortDirection();
        });

        // computation
        const result = ko.ignoreDependencies(() => this.filterExpressionObjectCalculation(elements, sortElements));

        return result;
    }

    public rearrange() {
        const filterElements = this.elements();
        const sortElements = this.sortElements();

        const notEmptyFilterElements = filterElements.filter((f) => !this.isEmptyFilterElement(f));
        const notEmptySortElements = sortElements.filter((s) => !this.isEmptySortElement(s));

        this.elements(notEmptyFilterElements);
        this.sortElements(notEmptySortElements);

        while (this.elements().length > this.sortElements().length)
            this.addNewSortElement(this.sortElements().length, false);

        while (this.sortElements().length > this.elements().length)
            this.addNewElement(this.elements().length, false);

        // Leerzeile
        this.addNewElement();
    }

    private filterExpressionObjectCalculation(elements: Array<FilterElementViewModelT>, sortElements: Array<FilterSortElementViewModelT>): FilterJson {
        const result = {} as FilterJson;

        const lastElement = _.last(elements);
        const validElements = elements
            .filter(element => !!element.filterFactory())
            .filter(element => this.isValidFilterElement(element, lastElement === element));

        const notEmptySortElements = sortElements.filter(element => !this.isEmptySortElement(element));

        // Serialisierung des Filters
        let serializedFilterElements = [];
        let serializedSortElements = [];
        const serializedChips = this.filterChipsViewModel.activeChips();

        if (!_.isEmpty(validElements)) {
            result.components = validElements.map(element => this.convertFilterElementToFilterComponent(element));
            serializedFilterElements = validElements.map(element => this.createFilterElementForSerializedFilter(element));
        }
        if (!_.isEmpty(notEmptySortElements)) {
            result.sorting = notEmptySortElements.map(element => this.convertFilterSortElementToSortElement(element));
            serializedSortElements = notEmptySortElements.map(element => this.createSortElementForSerializedFilter(element));
        }

        const serializedFilter: ISerializedFilter = {
            filterElements: serializedFilterElements,
            sortElements: serializedSortElements,
            activeChips: serializedChips,
            isVisible: this.isVisible(),
            editPublic: this.editPublicChips()
        };
        this.serializedFilter(JSON.stringify(serializedFilter));

        return result;
    }

    private isValidFilterElement(element: FilterElementViewModelT, isLastElement: boolean): boolean {
        const filterInfo = element.filterFactory().getFilterInfo() as FilterInfo;
        const operatorSelection = element.dropDownListOperators.selected();

        let isValid = !!operatorSelection && Utilities.isNotBlank(operatorSelection.value); // has operator
        isValid = isValid && (Utilities.isNotBlank(element.dropDownListConnectors.selected()) || isLastElement); // has connector
        isValid = isValid && Utilities.isNotBlank(filterInfo.value); // has value
        isValid = isValid && Utilities.isNotBlank(filterInfo.field); // has field
        isValid = isValid && element.filterFactory().isValid();              

        return isValid;
    }

    private isEmptyFilterElement(element: FilterElementViewModelT): boolean {
        const operatorSelection = element.dropDownListOperators.selected();

        let isEmpty = Utilities.isBlank(element.column());
        isEmpty = isEmpty && (!operatorSelection || Utilities.isBlank(operatorSelection.value));

        return isEmpty;
    }

    private isEmptySortElement(element: FilterSortElementViewModelT): boolean {
        return Utilities.isBlank(element.sortColumn());
    }

    private convertFilterElementToFilterComponent(element: FilterElementViewModelT): FilterComponent {
        const filterInfo = element.filterFactory().getFilterInfo() as FilterInfo;

        let operator = element.dropDownListOperators.selected().value;

        // Wenn null check gemacht werden muss, muss "is (not) null" statt einem equals-Operator genutzt werden.
        // filterInfo ist meistens null, deswegen == true
        if (filterInfo?.nullCheck == true) {
            if (operator === "!=") {
                operator = "is not null";
            }
            else if (operator === "=") {
                operator = "is null";
            }
        }
        
        const filterTerm = {} as FilterTerm;
        filterTerm.property = filterInfo.field;
        filterTerm.value = filterInfo.value;
        filterTerm.operator = operator;

        const filterComponent = {} as FilterComponent;
        filterComponent.connector = element.connector();
        filterComponent.filter = filterTerm;

        return filterComponent;
    }

    private convertFilterSortElementToSortElement(element: FilterSortElementViewModelT): SortComponent {
        const column = element.sortColumn();
        const direction = element.sortDirection();

        return {
            sortColumn: column,
            sortDirection: direction
        };
    }

    private createFilterElementForSerializedFilter(element: FilterElementViewModelT) {
        const factory = element.filterFactory();
        const column = element.dropDownListColumns.value();

        return {
            operator: element.dropDownListOperators.value(),
            connector: element.dropDownListConnectors.value(),
            value: factory.getValueForFilter(),
            column: column
        }
    }

    private createSortElementForSerializedFilter(element: FilterSortElementViewModelT) {
        return {
            sortColumn: element.sortColumn(),
            sortDirection: element.sortDirection()
    }
    }

    public getFilterExpressionForExport(): string {
        const elements = this.elements().filter((x) => x.isComplete());
        const filter = [] as Array<string>;

        elements.forEach((el, i) => {
            filter.push(el.getFilterExpression(elements.length !== (i + 1)));
        });

        return filter.join(' ');
    }

    /**
     * Erzeugt ein neues FilterElementViewModel, das mit diesem Filter verbunden ist.
     * Wird kein Index übergeben, wird das neue Element an das Ende der Elemente angehängt,
     * Ansonsten wird es an der definierten Stelle eingefügt.
     * 
     * @param index position to add the element at
     * @param addSortElement flag to control wether a sortElement should also be added
     */
    public addNewElement(index?: number, addSortElement: boolean = true): void {
        this.addNewElementWithParameters({}, index, addSortElement);
    }

    /**
     * Adds a new Filter Element to the FilterElements Collection
     * 
     * @param index position to add the element at
     * @param addSortElement flag to control wether a sortElement should also be added
     */
    public addNewElementWithParameters(params: object, index: number = this.elements.peek().length, addSortElement: boolean = true) {
        const FilterElementViewModel = ComponentManager.getViewModel<typeof FilterElementViewModelT>('FilterElementViewModel');
        params = $.extend(params, {
            parentLocator: this.locator,
            locator: String(index),
            filter: this
        });

        this.elements.splice(index, 0, new FilterElementViewModel(params));

        if (addSortElement) {
            this.addNewSortElement(index, false);
        }
    }

    /**
     * Adds a new Sort Element to the SortElements Collection
     * 
     * @param index position to add the element at
     * @param addFilterElement flag to control wether a filterElement should also be added
     */
    public addNewSortElement(index: number = this.sortElements.peek().length, addFilterElement: boolean = true) {
        this.addNewSortElementWithParams({ filterViewModel: this }, index, addFilterElement);
    }


    public addNewSortElementWithParams(params, index: number = this.sortElements.peek().length, addFilterElement: boolean = true): void {
        const FilterSortElementViewModel = ComponentManager.getViewModel<typeof FilterSortElementViewModelT>('FilterSortElementViewModel');
        params = $.extend(params, {
            parentLocator: this.defineComputed(() => {
                return this.locator() + '.sort';
            }),
            locator: String(index),
            filterViewModel: this,
            isEnabled: this.shouldEnableSorting
        });

        const filterSortElementVm = new FilterSortElementViewModel(params);

        this.sortElements.splice(index, 0, filterSortElementVm);

        if (addFilterElement) {
            this.addNewElement(index, false);
        }
    }

    public removeFilterElement() {
        if (_.isEmpty(this.elements())) {
            return;
        }


        const isSingleElement = this.elements().length === 1;

        const deleteAndDispose = (obsArray) => {
            const splicedElement = obsArray.splice(obsArray().length - 2, 1);
            splicedElement.forEach(e => { if (_.isFunction(e.dispose)) e.dispose(); });
        };

        if (!isSingleElement) {
            deleteAndDispose(this.elements);
            deleteAndDispose(this.sortElements);
        }

        this.refreshSorting();
    }

    public resetFilter() {
        const sortElements = this.sortElements();
        const filterElements = this.elements();
        const isEmptyFilter = Utilities.isNullOrUndefined(this.filterExpressionObject().components);

        this.sortElements([]);
        this.elements([]);

        sortElements.forEach((e) => e.dispose());
        filterElements.forEach((e) => e.dispose());

        if (!_.isEqual(this.orderBy(), [])) {
            this.orderBy([]);
            this.orderBy.valueHasMutated();
        }

        if (!isEmptyFilter) {
            this.notifyFilterExpressionObject.valueHasMutated();
        }

        this.filterNameTextBoxViewModel.resetValue();
    }

    /**
     * Entfernt die aktuellen Sortierelemente
     */
    private removeSortElements() {
        const sortElements = this.sortElements();
        this.sortElements([]);
        sortElements.forEach((e, i) => e.dispose());
    }

    /**
     * Entfernt die aktuellen Sortierelement und setzt die Sortierung zurück
     * @param suspend
     */
    public resetSortElements(suspend: boolean = false) {
        this.removeSortElements();
        this.doSuspended(() => this.orderBy([]), this, 'suspendOrderByUpdateToGrid');
    }

    public refreshSorting() {
        const orderBy = this.orderBy();
        const wasEmptyOrdering = _.isEmpty(orderBy);

        let isChanged = orderBy.some((ordering: SortElement, index) =>
            ordering.dir !== this.sortElements()[index].sortDirectionLink.direction() ||
            ordering.field !== this.sortElements()[index].sortColumnList.value()
        );

        const unemptySortElements = this.sortElements().filter(sortElement => sortElement.hasSortColumn());
        isChanged = isChanged || unemptySortElements.length !== orderBy.length;

        // update orderBy
        unemptySortElements
            .forEach((sortElement, index) => orderBy[index] = {
                dir: sortElement.sortDirectionLink.direction(),
                field: sortElement.sortColumnList.value()
            });

        if (unemptySortElements.length < orderBy.length) {
            orderBy.length = unemptySortElements.length;
        }

        this.orderBy(orderBy);

        if (_.isEmpty(unemptySortElements) && !_.isEmpty(this.orderBy())) {
            this.orderBy([]);
        }

        if (isChanged || (wasEmptyOrdering && !_.isEmpty(orderBy)) || orderBy.length !== unemptySortElements.length) {
            this.orderBy.valueHasMutated();
        }
    }

    private doSuspended<T>(fn, obj: T, flag: keyof T) {
        Utilities.wrap(
            () => obj[flag] = true as any,
            fn,
            () => obj[flag] = false as any
        );
    }

    public persistExtentCountForActiveChips(count: number): void {
        let activeChips = this.filterChipsViewModel.activeChips();
        let filteredActiveChips = activeChips.filter(chipTitle =>
            chipTitle !== FilterChipsViewModelT.getPreFilterLabel() && !FilterChipsHelper.isCustom(chipTitle));

        if (this.editPublicChips()) {
            filteredActiveChips = filteredActiveChips.filter(x => FilterChipsHelper.isPublic(x));   
        }
        else {
            filteredActiveChips = filteredActiveChips.filter(x => FilterChipsHelper.isPrivate(x));
        }

        filteredActiveChips.forEach(activeChip => {
            let filter: FilterJson = this.filterChipsViewModel.loadNamedFilter(activeChip);
            filter.lastExtentCount = count;

            this.filterChipsViewModel.persistFilterForName(activeChip, filter);
        });
    }

    public suspendOrderByUpdateToGrid = false;

    public updatePublicChips(): void {
        this.filterChipsViewModel.updatePublicUserData();
    }

    public serializeActiveChips(chips: string[]): void {

        var serializedFilter: ISerializedFilter = null;
        if (Utilities.isNullOrUndefinedOrBlank(this.serializedFilter())) {
            serializedFilter = {
                filterElements: [],
                sortElements: [],
                activeChips: chips,
                isVisible: this.isVisible(),
                editPublic: this.editPublicChips()
            };
        } else {
            serializedFilter = JSON.parse(this.serializedFilter());
            serializedFilter.activeChips = chips;
        }
        this.serializedFilter(JSON.stringify(serializedFilter));
    }
}

ComponentManager.setViewModel('FilterViewModel', FilterViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');